import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, Tag, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import eyeIcon from "../../../Assets/Icons/eye.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import HighRiskComponent from "../../../Components/HighRiskComponent";
import courseUp from "../../../Assets/Icons/Course Up.svg";
import courseDown from "../../../Assets/Icons/Course Down.svg";
import car from "../../../Assets/Vehicle Images/total vehicle.png";
import { getVehicleStatus } from "../../../API/User/vehicleStatus";
import { TrendTag } from "../Dashboard";

const VehicleStatus = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [vehicleData, setVehicleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highRiskVehicles, setHighRiskVehicles] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [onlineVehiclesChange, setOnlineVehiclesChange] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await getVehicleStatus();
      const data = response?.data?.data;
      const vehicles = data?.vehicles ?? [];
      const formattedData = vehicles.map((v, index) => ({
        key: index + 1,
        fullData: v,
        customer_name: v?.customer?.customer_name ?? "Unknown",
        vehicle_name: v?.vehicle?.vehicle_name ?? "Unknown",
        status: v?.status ?? "offline",
        risk_score: v?.score?.total_score ?? 0,
      }));
      setVehicleData(formattedData);
      setThreshold(data?.threshold);
      setHighRiskVehicles(data?.high_risk_vehicles)
      setOnlineVehiclesChange(data?.total_vehicles_change)

    } catch (error) {
      message.error("Failed to load vehicle data");
      console.error(error);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle_name"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
      ),
      filters: [
        { text: "Online", value: "online" },
        { text: "Offline", value: "offline" },
      ],
      onFilter: (value, record) => record.status === value,
    },

    {
      title: "High-Risk Activity Score",
      dataIndex: "risk_score",
      key: "risk_score",
      sorter: (a, b) => a.risk_score - b.risk_score,
      render: (risk_score) => {
        let colorClass = "";

        if (risk_score < 100) {
          colorClass = "low";
        } else if (risk_score >= 100 && risk_score <= 129) {
          colorClass = "moderate";
        } else if (risk_score >= 130 && risk_score <= 159) {
          colorClass = "high";
        } else if (risk_score >= 160 && risk_score <= 200) {
          colorClass = "very-high";
        }

        return <span className={`risk-badge ${colorClass}`}>{risk_score}</span>;
      },
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <a
          onClick={() =>
            navigate(`/view-high-risk-detail/${record.vehicle_name}`, {
              state: { ...record.fullData, pageTitle: "Vehicle Details" },
            })
          }
        >
          <img src={eyeIcon} alt="view" />
        </a>
      ),
    },
  ];

  return (
    <div className="vehicle-status">
      <div className="row">
        <div className="tabs-container">
          <div className="tab-item tab-black">
            <div className="tab-title">Total Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">{vehicleData.length}</div>
                <div className="tab-tag">
                  <TrendTag trend={onlineVehiclesChange} />
                </div>
              </div>
              <div className="right">
                <img src={car} alt="car image" />
              </div>
            </div>
          </div>

          <HighRiskComponent high_risk_vehicles={highRiskVehicles} threshold={threshold} />
        </div>
      </div>

      <div className="table">
        <GenericTable
          title="Vehicle Status"
          dataSource={vehicleData}
          columns={customerColumns}
          rowKey="key"
        />
      </div>
    </div>
  );
};

export default VehicleStatus;
